import { useEffect, useMemo } from 'react';

import { keepPreviousData, useQueries } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { getNextPageParam } from '@pkg/hooks/deprecated';

import { CollectionChainType, ItemType, type PaginatedResult } from '@app/client';
import { callGetWishlistItems, WISHLIST_ITEMS_CACHE } from '@app/client/resources/wishlists';
import { type WishlistItem } from '@app/client/resources/wishlists/types';
import { useWishlistItemStore } from '@app/store/zustand';

interface FetchItemsArgs {
    updateStore?: boolean;
    disabled?: boolean;
    providedSlug?: string;
}
const DEFAULT_WISHLIST_ITEMS_LIMIT = 50;
export function useFetchWishlistItems({ providedSlug, disabled, updateStore = false }: FetchItemsArgs) {
    const { wishlistSlug: currentStoredSlug, setWishlistItems } = useWishlistItemStore();
    const { wishlistSlugParam } = useParams();
    const limit = DEFAULT_WISHLIST_ITEMS_LIMIT;
    const chains = Object.values(CollectionChainType);

    const slug = providedSlug || wishlistSlugParam || '';

    const cache = [WISHLIST_ITEMS_CACHE, slug];

    const getOptions = (network: CollectionChainType, type: ItemType) => ({
        queryKey: [...cache, type, network],
        retry: false,
        placeHolderData: keepPreviousData,
        getNextPageParam,
        enabled: Boolean(slug?.length),
        queryFn: ({ signal, pageParam = 0 }: { signal?: AbortSignal; pageParam?: number }) =>
            callGetWishlistItems({
                wishlist_id: slug,
                type,
                query: { page: pageParam || 0, limit, network },
                settings: { signal },
            }) as Promise<PaginatedResult<WishlistItem>>,
    });

    const tokenItemsRes = useQueries({
        queries: chains.map((chain) => {
            const options = getOptions(chain, ItemType.Token);
            return {
                ...options,
            };
        }),
    });
    const collectionItemsRes = useQueries({
        queries: chains.map((chain) => {
            const options = getOptions(chain, ItemType.Collection);
            return {
                ...options,
            };
        }),
    });

    const itemsRes = [...collectionItemsRes, ...tokenItemsRes];

    const collectionItems = useMemo(
        () =>
            collectionItemsRes
                ?.flatMap((r) => {
                    const response = r?.data as unknown as PaginatedResult<WishlistItem>;
                    if (response && response.data) {
                        return response.data;
                    }
                })
                .filter((item) => !!item) as unknown as WishlistItem[],
        [collectionItemsRes],
    );
    const tokenItems = useMemo(
        () =>
            tokenItemsRes
                ?.flatMap((r) => {
                    const response = r?.data as unknown as PaginatedResult<WishlistItem>;
                    if (response && response.data) {
                        return response.data;
                    }
                })
                .filter((item) => !!item) as unknown as WishlistItem[],
        [tokenItemsRes],
    );

    const isLoading = itemsRes.flatMap((res) => res.isLoading)?.some((loading) => loading);
    const isFetching = itemsRes.flatMap((res) => res.isFetching)?.some((fetching) => fetching);
    const isPlaceholderData = itemsRes.flatMap((res) => res.isPlaceholderData)?.some((previousData) => previousData);

    useEffect(() => {
        if (
            slug?.length &&
            !disabled &&
            updateStore &&
            currentStoredSlug !== slug &&
            (tokenItems?.length || collectionItems?.length)
        ) {
            setWishlistItems({
                wishlistSlug: slug,
                wishlistItems: {
                    tokenItems,
                    collectionItems,
                },
            });
        }
    }, [collectionItems, currentStoredSlug, setWishlistItems, tokenItems, updateStore, slug, disabled]);

    return { collectionItems, tokenItems, isLoading, isFetching, isPlaceholderData };
}
